import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.js';
import { auth } from '../auth/firebase.js';
import type { Comment, Invoice, InvoicePage } from './types.js';

export type Direction = NonNullable<Invoice['direction']>;
export type State = NonNullable<Invoice['state']>;

export const DIRECTIONS: readonly Direction[] = ['INBOUND', 'OUTBOUND', 'OFF_PLATFORM'] as const;

export const STATES: readonly State[] = [
  'RECEIVED',
  'PROCESSING',
  'AVAILABLE',
  'ACKNOWLEDGED',
  'REJECTED',
  'VOIDED',
  'SEND_PENDING',
  'SEND_UNCONFIRMED',
  'SEND_ACCEPTED',
  'SEND_REJECTED',
  'LEGACY_ONLY',
] as const;

export type SortKey = 'sender.name' | 'invoiceNumber' | 'amountTotal' | 'dueDate' | 'state';
export type SortDir = 'asc' | 'desc';

export const SORT_KEYS: readonly SortKey[] = [
  'sender.name',
  'invoiceNumber',
  'amountTotal',
  'dueDate',
  'state',
] as const;

export interface InvoiceListFilters {
  direction: Direction | null;
  /**
   * Selected state filter values. Empty set = no state filter. Multi-value
   * is a FE-only convenience: the BE contract `GET /api/invoices` accepts
   * a single `?state=…` only, so >1 states fan out via useQueries and the
   * results merge client-side. See useInvoices below.
   */
  states: State[];
  q: string;
  /** ISO date (YYYY-MM-DD) lower bound on `invoice.invoiceDate`. Null = no lower bound. */
  dateFrom: string | null;
  /** ISO date (YYYY-MM-DD) upper bound on `invoice.invoiceDate`. Null = no upper bound. */
  dateTo: string | null;
  /** 9-digit SIREN substring for client-side `sender.siren` filter. Empty = no filter. */
  senderSiren: string;
  /**
   * Client-side column sort. The contract list endpoint has no sort
   * params, so this is applied over the loaded page in `applyClientFilters`
   * after the date/SIREN filters. `null` keeps the BE's default order.
   */
  sortBy: SortKey | null;
  sortDir: SortDir;
}

export const EMPTY_FILTERS: InvoiceListFilters = {
  direction: null,
  states: [],
  q: '',
  dateFrom: null,
  dateTo: null,
  senderSiren: '',
  sortBy: null,
  sortDir: 'asc',
};

const STATE_RANK: Record<State, number> = STATES.reduce(
  (acc, s, i) => {
    acc[s] = i;
    return acc;
  },
  {} as Record<State, number>,
);

function compareInvoices(a: Invoice, b: Invoice, key: SortKey): number {
  switch (key) {
    case 'sender.name':
      return (a.sender?.name ?? '').localeCompare(b.sender?.name ?? '', 'fr');
    case 'invoiceNumber':
      return (a.invoiceNumber ?? '').localeCompare(b.invoiceNumber ?? '', 'fr');
    case 'amountTotal':
      return (a.amountTotal ?? 0) - (b.amountTotal ?? 0);
    case 'dueDate': {
      // nulls last (in asc order). Descending flips this below in
      // applyClientSort via `dir === 'desc' ? -cmp : cmp` but for nulls
      // we keep them last in both directions for UX.
      const ad = a.dueDate;
      const bd = b.dueDate;
      if (!ad && !bd) return 0;
      if (!ad) return 1;
      if (!bd) return -1;
      return ad.localeCompare(bd);
    }
    case 'state':
      return (STATE_RANK[a.state] ?? 0) - (STATE_RANK[b.state] ?? 0);
  }
}

function applyClientSort(items: Invoice[], filters: InvoiceListFilters): Invoice[] {
  if (!filters.sortBy) return items;
  const out = [...items];
  out.sort((a, b) => {
    const cmp = compareInvoices(a, b, filters.sortBy!);
    if (filters.sortBy === 'dueDate') {
      // Keep nulls last regardless of direction.
      const ad = a.dueDate;
      const bd = b.dueDate;
      if (!ad && !bd) return 0;
      if (!ad) return 1;
      if (!bd) return -1;
    }
    return filters.sortDir === 'desc' ? -cmp : cmp;
  });
  return out;
}

function buildQuery(
  tenantId: string,
  base: { direction: Direction | null; q: string },
  state: State | null,
  cursor: string | null,
): string {
  const params = new URLSearchParams({ tenantId, limit: '50' });
  if (base.direction) params.set('direction', base.direction);
  if (state) params.set('state', state);
  if (base.q.trim()) params.set('q', base.q.trim());
  if (cursor) params.set('cursor', cursor);
  return `/invoices?${params.toString()}`;
}

function applyClientFilters(items: Invoice[], filters: InvoiceListFilters): Invoice[] {
  let out = items;
  // Date range — filters by invoiceDate (the "Émise" column).
  const from = filters.dateFrom && filters.dateFrom <= (filters.dateTo ?? '￿')
    ? filters.dateFrom
    : filters.dateTo && filters.dateFrom && filters.dateFrom > filters.dateTo
      ? filters.dateTo // swap silently
      : filters.dateFrom;
  const to = filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo
    ? filters.dateFrom // swap silently
    : filters.dateTo;
  if (from) out = out.filter((i) => (i.invoiceDate ?? '') >= from);
  if (to) out = out.filter((i) => (i.invoiceDate ?? '￿') <= to);
  if (filters.senderSiren.trim()) {
    const needle = filters.senderSiren.trim();
    out = out.filter((i) => (i.sender?.siren ?? '').includes(needle));
  }
  return out;
}

/**
 * List invoices with multi-state, date range, and SIREN search applied.
 * Multi-state is fanned out across N parallel BE queries (contract has
 * single-value `?state=` only) and merged client-side. Date range and
 * SIREN search are pure client-side filters over the loaded page(s).
 */
export function useInvoices(tenantId: string, filters: InvoiceListFilters, cursor: string | null) {
  const states = filters.states;
  const baseFiltersKey = `${filters.direction ?? ''}|${filters.q}`;

  // Fan-out path: ≥2 states selected → run one query per state, merge.
  const fanoutQueries = useQueries({
    queries: (states.length > 1 ? states : []).map((s) => ({
      queryKey: ['invoices', tenantId, baseFiltersKey, s, cursor] as const,
      queryFn: () =>
        apiFetch<InvoicePage>(buildQuery(tenantId, filters, s, cursor)),
      enabled: Boolean(tenantId),
      placeholderData: keepPreviousData,
      staleTime: 15_000,
    })),
  });

  // Single-query path: 0 or 1 state.
  const singleState = states.length === 1 ? states[0]! : null;
  const singleQuery = useQuery<InvoicePage>({
    queryKey: ['invoices', tenantId, baseFiltersKey, singleState, cursor],
    queryFn: () => apiFetch<InvoicePage>(buildQuery(tenantId, filters, singleState, cursor)),
    enabled: Boolean(tenantId) && states.length <= 1,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  if (states.length > 1) {
    const isLoading = fanoutQueries.some((r) => r.isLoading);
    const isError = fanoutQueries.every((r) => r.isError);
    const isFetching = fanoutQueries.some((r) => r.isFetching);
    const seen = new Set<string>();
    const merged: Invoice[] = [];
    for (const r of fanoutQueries) {
      for (const inv of r.data?.items ?? []) {
        if (!seen.has(inv.primaryKey)) {
          seen.add(inv.primaryKey);
          merged.push(inv);
        }
      }
    }
    const filtered = applyClientSort(applyClientFilters(merged, filters), filters);
    return {
      data: { items: filtered, nextCursor: null } satisfies InvoicePage,
      isLoading,
      isError,
      isFetching,
      refetch: () => fanoutQueries.forEach((r) => void r.refetch()),
    };
  }

  // Single path: return shape matches react-query useQuery (with client-side filter applied).
  const data: InvoicePage | undefined = singleQuery.data
    ? {
        items: applyClientSort(
          applyClientFilters(singleQuery.data.items, filters),
          filters,
        ),
        nextCursor: singleQuery.data.nextCursor,
      }
    : undefined;
  return {
    data,
    isLoading: singleQuery.isLoading,
    isError: singleQuery.isError,
    isFetching: singleQuery.isFetching,
    refetch: () => void singleQuery.refetch(),
  };
}

export function useInvoice(primaryKey: string) {
  return useQuery<Invoice>({
    queryKey: ['invoice', primaryKey],
    queryFn: () => apiFetch<Invoice>(`/invoices/${encodeURIComponent(primaryKey)}`),
    enabled: Boolean(primaryKey),
    staleTime: 30_000,
  });
}

export function useComments(primaryKey: string) {
  return useQuery<Comment[]>({
    queryKey: ['comments', primaryKey],
    queryFn: () => apiFetch<Comment[]>(`/invoices/${encodeURIComponent(primaryKey)}/comments`),
    enabled: Boolean(primaryKey),
    staleTime: 30_000,
  });
}

/**
 * Resolve the GCS signed URL for an invoice artifact and open it in a new
 * tab. The BE endpoint returns a 302; the browser follows it, so the
 * final `res.url` carries the signed URL we want to surface.
 *
 * Note: this fetches the artifact bytes once to satisfy the redirect and
 * then re-opens the URL — wasteful by a few hundred ms on first click,
 * but avoids a contract change to surface the signed URL in a JSON body.
 * Acceptable for S1.5; revisit if perf matters.
 */
/**
 * Optimistic add of an internal comment. Inserts a synthetic comment at the
 * top of the cached thread with a `pending` flag (negative createdAt so it
 * always sorts first in DESC order isn't needed — we insert directly into
 * position 0 of the cached array). On success, the server-returned doc
 * replaces the synthetic; on error, the synthetic is removed and the caller
 * receives the rejection so it can surface a toast and preserve the text.
 */
export interface PendingComment extends Comment {
  pending: true;
}

export function isPendingComment(c: Comment | PendingComment): c is PendingComment {
  return 'pending' in c && (c as PendingComment).pending === true;
}

interface AddCommentVars {
  body: string;
  optimistic: PendingComment;
}

export function useAddComment(primaryKey: string) {
  const qc = useQueryClient();
  return useMutation<Comment, Error, AddCommentVars, { previous: Comment[] | undefined }>({
    mutationFn: ({ body }) =>
      apiFetch<Comment>(`/invoices/${encodeURIComponent(primaryKey)}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      }),
    onMutate: async ({ optimistic }) => {
      await qc.cancelQueries({ queryKey: ['comments', primaryKey] });
      const previous = qc.getQueryData<Comment[]>(['comments', primaryKey]);
      qc.setQueryData<Comment[]>(['comments', primaryKey], [optimistic, ...(previous ?? [])]);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(['comments', primaryKey], ctx.previous);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['comments', primaryKey] }),
  });
}

export function useDeleteComment(primaryKey: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string, { previous: Comment[] | undefined }>({
    mutationFn: (commentId) =>
      apiFetch<void>(
        `/invoices/${encodeURIComponent(primaryKey)}/comments/${encodeURIComponent(commentId)}`,
        { method: 'DELETE' },
      ),
    onMutate: async (commentId) => {
      await qc.cancelQueries({ queryKey: ['comments', primaryKey] });
      const previous = qc.getQueryData<Comment[]>(['comments', primaryKey]);
      qc.setQueryData<Comment[]>(
        ['comments', primaryKey],
        (previous ?? []).filter((c) => c.commentId !== commentId),
      );
      return { previous };
    },
    onError: (_err, _commentId, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(['comments', primaryKey], ctx.previous);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['comments', primaryKey] }),
  });
}

/**
 * Optimistic assignee mutation. Flips `invoice.assignedTo` immediately,
 * reverts on error, invalidates on settle so the server-returned doc
 * reconciles.
 */
export function useAssignInvoice(primaryKey: string) {
  const qc = useQueryClient();
  return useMutation<Invoice, Error, string | null, { previous: Invoice | undefined }>({
    mutationFn: (uid) =>
      apiFetch<Invoice>(`/invoices/${encodeURIComponent(primaryKey)}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid }),
      }),
    onMutate: async (uid) => {
      await qc.cancelQueries({ queryKey: ['invoice', primaryKey] });
      const previous = qc.getQueryData<Invoice>(['invoice', primaryKey]);
      if (previous) {
        qc.setQueryData<Invoice>(['invoice', primaryKey], { ...previous, assignedTo: uid });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(['invoice', primaryKey], ctx.previous);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['invoice', primaryKey] }),
  });
}

export interface DisputePayload {
  reason: string;
  comment: string;
}

/**
 * Optimistic dispute submission. Flips the cached invoice's `dispute`
 * field + state to a synthetic `DISPUTED`-shaped object so the detail
 * view re-renders the dispute panel before the server confirms. Reverts
 * on error.
 */
export function useDisputeInvoice(primaryKey: string) {
  const qc = useQueryClient();
  return useMutation<Invoice, Error, DisputePayload, { previous: Invoice | undefined }>({
    mutationFn: (payload) =>
      apiFetch<Invoice>(`/invoices/${encodeURIComponent(primaryKey)}/dispute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: ['invoice', primaryKey] });
      const previous = qc.getQueryData<Invoice>(['invoice', primaryKey]);
      if (previous) {
        const optimistic: Invoice = {
          ...previous,
          dispute: {
            reason: payload.reason as Invoice['dispute'] extends
              | { reason: infer R }
              | null
              | undefined
              ? R
              : never,
            comment: payload.comment,
            byUid: previous.dispute?.byUid ?? 'pending',
            byEmail: previous.dispute?.byEmail ?? null,
            byDisplayName: previous.dispute?.byDisplayName ?? null,
            requestedAt: new Date().toISOString(),
            sentToIopoleAt: null,
          },
        };
        qc.setQueryData<Invoice>(['invoice', primaryKey], optimistic);
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(['invoice', primaryKey], ctx.previous);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['invoice', primaryKey] }),
  });
}

export async function openArtifact(primaryKey: string, artifact: string): Promise<void> {
  const u = auth.currentUser;
  const token = u ? await u.getIdToken(false) : null;
  const res = await fetch(
    `/api/invoices/${encodeURIComponent(primaryKey)}/file/${encodeURIComponent(artifact)}`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      redirect: 'follow',
    },
  );
  if (!res.ok) {
    throw new Error(`Artifact fetch failed: ${res.status}`);
  }
  window.open(res.url, '_blank', 'noopener,noreferrer');
}
